import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/40 dialog-overlay",
      "will-change-[opacity]",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { hideCloseButton?: boolean }
>(({ className, children, onInteractOutside, hideCloseButton, ...props }, ref) => {
  // Prevent Dialog's focus trap from closing portaled Radix components (Select, Popover, DatePicker).
  // When these open via Portal, Dialog's DismissableLayer detects the interaction as "outside"
  // and immediately dismisses the dropdown. This handler blocks that for Radix popper portals.
  const handleInteractOutside = React.useCallback(
    (e: { target: EventTarget | null; preventDefault: () => void }) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest?.('[data-radix-popper-content-wrapper]')) {
        e.preventDefault();
      }
      onInteractOutside?.(e as any);
    },
    [onInteractOutside]
  );

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed z-50 border-0 bg-white dark:bg-gray-900 overflow-hidden dialog-content",
          // Mobile: iPhone-style bottom sheet drawer (animations handled by CSS in tailwind.css)
          "bottom-0 left-0 right-0 w-full max-h-[85vh] p-0 rounded-t-[20px] shadow-[0_-10px_40px_-5px_rgba(0,0,0,0.15)]",
          // Desktop: centered modal override (transform centering handled by CSS in tailwind.css)
          // max-h-[90vh] caps the height to the viewport so tall modals stay fully
          // on-screen (title + footer reachable) and scroll internally — the old
          // md:max-h-none let content overflow the screen on shorter displays.
          "md:bottom-auto md:left-[50%] md:top-[50%] md:right-auto md:w-full md:max-w-2xl md:max-h-[90vh] md:p-6 md:rounded-3xl md:shadow-[0_25px_80px_-12px_rgba(0,0,0,0.25)]",
          className
        )}
        style={{
          backfaceVisibility: 'hidden',
        }}
        onInteractOutside={handleInteractOutside}
        {...props}
      >
        {/* Mobile drag handle indicator */}
        <div className="flex justify-center pt-3 pb-1 shrink-0 md:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>
        {children}
        {/* Avoid `asChild` to prevent Radix composeRefs ref loops */}
        {!hideCloseButton && (
          <DialogPrimitive.Close
            className={cn(
              buttonVariants({ variant: "ghost", size: "icon" }),
              "absolute right-4 top-2 md:top-4 z-50 rounded-full nav-glass shadow-apple-sm",
              "hover:scale-105 active:scale-95 transition-all duration-150 ease-out",
              "hover:bg-white/40 dark:hover:bg-gray-800/40",
              "focus:outline-none focus:ring-0"
            )}
            style={{ touchAction: 'manipulation' }}
          >
            <X className="w-5 h-5 transition-transform duration-150" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 pt-4",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}

